import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import TextInput from "../../components/ui/TextInput";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField, DropdownField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { Tooltip } from "@mui/material";
import {
    getApiKeys,
    createApiKeyRequest,
    toggleApiKeyRequest,
    deleteApiKeyRequest,
    getCompaniesByDatabase,
    ApiKeyListItem,
} from "../../api/administration";
import Icon from "../../components/common/Icon";
import styles from "./ApiKeys.module.css";

const DATABASE_OPTIONS = [
    { value: "default", label: "Default" },
    { value: "colorbus", label: "Colorbus" },
    { value: "vdp", label: "Vedettes de Paris" },
    { value: "thoiry", label: "Thoiry" },
];

const ApiKeys: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [apiKeys, setApiKeys] = useState<ApiKeyListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
    const [companiesLoading, setCompaniesLoading] = useState<boolean>(false);
    const [, setSelectedDatabase] = useState<string>("");

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const keysRes = await getApiKeys(token);
            setApiKeys(keysRes);
        } catch (error) {
            console.error("Failed to fetch API keys", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompaniesForDatabase = useCallback(async (database: string) => {
        setCompanyOptions([]);
        setSelectedDatabase(database);
        if (!database) return;
        setCompaniesLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const companies = await getCompaniesByDatabase(token, database);
            setCompanyOptions(companies.map((c) => ({ value: c.id, label: c.name })));
        } catch (error) {
            console.error("Failed to fetch companies", error);
        } finally {
            setCompaniesLoading(false);
        }
    }, []);

    const handleToggle = async (id: string) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            await toggleApiKeyRequest(token, id);
            setApiKeys((prev) => prev.map((k) => (k.id === id ? { ...k, isActive: !k.isActive } : k)));
        } catch (error) {
            console.error("Failed to toggle API key", error);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            await deleteApiKeyRequest(token, id);
            setApiKeys((prev) => prev.filter((k) => k.id !== id));
        } catch (error) {
            console.error("Failed to delete API key", error);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return t("apiKeyNeverUsed");
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(t("apiKeyNameRequired")),
        database: Yup.string()
            .oneOf(DATABASE_OPTIONS.map((d) => d.value))
            .required(),
        companyId: Yup.string().required(t("apiKeyCompanyRequired")),
        serviceUserId: Yup.string(),
    });

    const filteredKeys = apiKeys.filter(
        (k) =>
            k.name.toLowerCase().includes(search.toLowerCase()) ||
            k.companyName.toLowerCase().includes(search.toLowerCase()),
    );

    if (loading) return <div>{t("loading")}</div>;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitleBare} ${adminSurface.pageTitleBare}`}>
                <TitleSubtitle title={t("apiKeys")} subtitle={t("apiKeysSubtitle")} />
            </div>

            <section className={`${adminSurface.section} ${adminSurface.sectionApi}`}>
                <h2 className={adminSurface.sectionTitle}>{t("createApiKey")}</h2>

                <Formik
                    initialValues={{ name: "", database: "", companyId: "", serviceUserId: "" }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const token = localStorage.getItem("token");
                            if (!token) return;

                            const result = await createApiKeyRequest(
                                token,
                                values.name,
                                values.database,
                                values.companyId,
                                values.serviceUserId,
                            );
                            setCreatedKey(result.key);
                            resetForm();
                            setCompanyOptions([]);
                            setSelectedDatabase("");
                            await fetchApiKeys();
                        } catch (error) {
                            console.error("Failed to create API key", error);
                        }
                    }}
                    validateOnMount
                >
                    {({ isSubmitting, errors, setFieldValue }) => (
                        <Form className={adminSurface.addForm}>
                            <div className={`${adminSurface.addFormGrid} ${adminSurface.addFormApi}`}>
                                <FormGroup name="name" title={t("apiKeyName")} required size="small">
                                    <Field name="name" component={TextField} placeholder={t("apiKeyName")} />
                                </FormGroup>

                                <FormGroup name="database" title={t("apiKeyDatabase")} required size="small">
                                    <Field
                                        name="database"
                                        component={DropdownField}
                                        options={DATABASE_OPTIONS}
                                        placeholder={t("selectDatabase")}
                                        isWithoutMargin
                                        onChange={(value: string) => {
                                            setFieldValue("companyId", "");
                                            setFieldValue("serviceUserId", "");
                                            fetchCompaniesForDatabase(value);
                                        }}
                                    />
                                </FormGroup>

                                <FormGroup name="companyId" title={t("apiKeyCompany")} required size="small">
                                    <Field
                                        name="companyId"
                                        component={DropdownField}
                                        options={companyOptions}
                                        placeholder={t("selectCompany")}
                                        loading={companiesLoading}
                                        disabled={companyOptions.length === 0 && !companiesLoading}
                                        isWithoutMargin
                                    />
                                </FormGroup>

                                <FormGroup name="serviceUserId" title={t("apiKeyUserId")} size="small">
                                    <Field
                                        name="serviceUserId"
                                        component={TextField}
                                        placeholder={t("apiKeyUserIdPlaceholder")}
                                    />
                                </FormGroup>

                                <div>
                                    <Tooltip
                                        title={
                                            Object.keys(errors).length > 0
                                                ? t("formIncompleteTooltip", {
                                                      fields: Object.values(errors).join(", "),
                                                  })
                                                : ""
                                        }
                                        arrow
                                    >
                                        <button
                                            className={adminSurface.addButton}
                                            type="submit"
                                            disabled={isSubmitting || Object.keys(errors).length > 0}
                                        >
                                            <Icon name="ic-plus" className="ic" size={16} />
                                            {t("createApiKey")}
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>

            {createdKey && (
                <div
                    style={{
                        marginBottom: "20px",
                        padding: "16px",
                        backgroundColor: "#f0f9f0",
                        border: "1px solid #4caf50",
                        borderRadius: "8px",
                    }}
                >
                    <strong>{t("apiKeyCreated")}</strong>
                    <div
                        style={{
                            marginTop: "8px",
                            fontFamily: "monospace",
                            fontSize: "14px",
                            wordBreak: "break-all",
                            userSelect: "all",
                        }}
                    >
                        {createdKey}
                    </div>
                    <button
                        className={adminSurface.modifyButton}
                        style={{ marginTop: "8px" }}
                        onClick={() => navigator.clipboard.writeText(createdKey)}
                    >
                        {t("copy") || "Copy"}
                    </button>
                    <button
                        className={adminSurface.modifyButton}
                        style={{ marginTop: "8px", marginLeft: "8px" }}
                        onClick={() => setCreatedKey(null)}
                    >
                        {t("close") || "Close"}
                    </button>
                </div>
            )}

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchApiKey")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("apiKeyName")}</th>
                                <th>{t("apiKeyCompany")}</th>
                                <th>{t("apiKeyDatabase")}</th>
                                <th>{t("apiKeyKey")}</th>
                                <th>{t("apiKeyRequests")}</th>
                                <th>{t("apiKeyLastUsed")}</th>
                                <th>{t("apiKeyStatus")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredKeys.map((apiKey) => (
                                <tr key={apiKey.id}>
                                    <td>{apiKey.name}</td>
                                    <td>{apiKey.companyName}</td>
                                    <td>{apiKey.database}</td>
                                    <td style={{ fontFamily: "monospace" }}>{apiKey.maskedKey}</td>
                                    <td>{apiKey.requestCount.toLocaleString()}</td>
                                    <td>{formatDate(apiKey.lastUsedAt)}</td>
                                    <td>
                                        <span
                                            style={{
                                                padding: "2px 8px",
                                                borderRadius: "4px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                backgroundColor: apiKey.isActive ? "#e8f5e9" : "#fbe9e7",
                                                color: apiKey.isActive ? "#2e7d32" : "#c62828",
                                            }}
                                        >
                                            {apiKey.isActive ? t("apiKeyActive") : t("apiKeyInactive")}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.rowActs}>
                                            <Tooltip
                                                title={apiKey.isActive ? t("apiKeyInactive") : t("apiKeyActive")}
                                                arrow
                                            >
                                                <button
                                                    type="button"
                                                    className={`${styles.icBtn}${apiKey.isActive ? ` ${styles.icBtnFill}` : ""}`}
                                                    onClick={() => handleToggle(apiKey.id)}
                                                    aria-label={
                                                        apiKey.isActive ? t("apiKeyInactive") : t("apiKeyActive")
                                                    }
                                                >
                                                    <Icon
                                                        name={apiKey.isActive ? "ic-pause" : "ic-check"}
                                                        className="ic"
                                                    />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title={t("delete")} arrow>
                                                <button
                                                    type="button"
                                                    className={`${styles.icBtn} ${styles.icBtnDel}`}
                                                    onClick={() => {
                                                        const confirmed = window.confirm(
                                                            `${t("deleteApiKeyConfirmation")} (${apiKey.name})`,
                                                        );
                                                        if (confirmed) handleDelete(apiKey.id);
                                                    }}
                                                    aria-label={t("delete")}
                                                >
                                                    <Icon name="ic-trash" className="ic" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredKeys.length} {t("apiKeys")?.toLowerCase?.() || "keys"}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ApiKeys;
